// components/ProductHero.js
import React, { useState, useEffect, useRef } from 'react';
// Import your video file(s)
import heroVideo from "../../assets/videos/hero-video.mp4";
// Optional: Import WebM version for better browser compatibility

// Create a LazyVideo component for optimal video loading
const LazyVideo = ({ src, className, autoPlay = true, muted = true, playsInline = true, loop = true }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const videoRef = useRef(null);
  const containerRef = useRef(null);

  // Set up intersection observer to detect when video container is in viewport
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };
    
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      });
    }, options);
    
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    
    return () => {
      observer.disconnect();
    };
  }, []);

  // Handle video loading events
  const handleLoadedData = () => {
    setIsLoaded(true);
    if (autoPlay && videoRef.current) {
      videoRef.current.play().catch(error => {
        console.error("Error playing video:", error);
      });
    }
  };

  return (
    <div ref={containerRef} className={`${className} relative`}>
      {/* Loading state - show before video loads */}
      {(!isLoaded && isVisible && !hasError) && (
        <div className="absolute inset-0 bg-black flex items-center justify-center">
          <div className="w-16 h-16 border-4 border-red-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
      
      {/* Actual video element, only loaded when in viewport */}
      {isVisible && (
        <video
          ref={videoRef}
          className={`w-full h-full object-cover transition-opacity duration-700 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
          autoPlay={autoPlay}
          muted={muted}
          playsInline={playsInline}
          loop={loop}
          onLoadedData={handleLoadedData}
          onError={() => setHasError(true)}
          preload="auto"
        >
          <source src={src} type="video/mp4" />
          {/* Optional: Add WebM format for better browser compatibility */}
          {/* <source src={srcWebm} type="video/webm" /> */}
          Your browser does not support the video tag.
        </video>
      )}
      
      {/* Placeholder - show when video is not yet visible */}
      {!isVisible && (
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black"></div>
      )}
    </div>
  );
};

const ProductHero = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isVisible, setIsVisible] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  
  // Reference to the next section for direct scrolling
  const nextSectionRef = useRef(null);
  const heroSectionRef = useRef(null);

  useEffect(() => {
    setIsVisible(true);
    
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20,
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Extremely simple scroll down function
  const scrollToProducts = () => {
    setIsScrolling(true);
  
    if (nextSectionRef.current) {
      nextSectionRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    } else if (heroSectionRef.current) {
      const heroHeight = heroSectionRef.current.offsetHeight;
      window.scrollTo({
        top: heroHeight - 50,
        behavior: 'smooth'
      });
    }
  
    setTimeout(() => {
      setIsScrolling(false);
    }, 1000);
  };
  

  return (
    <div ref={heroSectionRef} className="relative min-h-[80vh] sm:min-h-[70vh] md:min-h-[70vh] overflow-hidden bg-gradient-to-br from-black via-gray-900 to-black">
      {/* Video Background with Lazy Loading */}
      <div className="absolute inset-0 w-full h-full">
        <LazyVideo
          src={heroVideo}
          className="absolute w-full h-full"
          autoPlay={true}
          muted={true}
          playsInline={true}
          loop={true}
        />
        
        {/* Dark overlay for better text readability */}
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
      </div>

      {/* Animated Grid Background - Optional, can be removed if not needed */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(to right, rgba(255,255,255,0.05) 1px, transparent 1px),
                           linear-gradient(to bottom, rgba(255,255,255,0.05) 1px, transparent 1px)`,
          backgroundSize: '50px 50px',
          transform: `translate(${mousePosition.x}px, ${mousePosition.y}px)`,
          transition: 'transform 0.2s ease-out'
        }}></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 md:py-24 lg:py-32">
        <div className="text-center mt-8 sm:mt-0">
          {/* Animated Title */}
          <h1 
            className={`text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-4 sm:mb-6 transform transition-all duration-1000 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <span className="inline-block bg-clip-text text-transparent bg-gradient-to-r from-white via-red-200 to-white animate-gradient-x">
              3D Printing
            </span>
            <br className="sm:hidden" />
            <span className="sm:hidden"> </span>
            <span className="inline-block mt-1 sm:mt-2 bg-clip-text text-transparent bg-gradient-to-r from-red-400 via-red-600 to-red-400 animate-gradient-x-reverse">
              Excellence
            </span>
          </h1>

          {/* Animated Subtitle */}
          <p 
            className={`text-lg sm:text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto mb-8 sm:mb-10 px-4 transform transition-all duration-1000 delay-300 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            Transform your ideas into reality with our cutting-edge 3D printing technology 
            and expert craftsmanship
          </p>

          {/* Animated Feature Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 max-w-4xl mx-auto mt-8 sm:mt-12">
            {[
              { icon: '🚀', title: 'Rapid Prototyping', desc: 'Quick iterations' },
              { icon: '⚡', title: 'Premium Quality', desc: 'High precision' },
              { icon: '🎨', title: 'Material Options', desc: 'Wide selection' },
              { icon: '🛠️', title: 'Custom Solutions', desc: 'Tailored to you' }
            ].map((feature, index) => (
              <div
                key={index}
                className={`bg-white/5 backdrop-blur-lg border border-white/10 rounded-xl p-3 sm:p-4 
                  transform transition-all duration-700 hover:scale-105 hover:bg-white/10
                  ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}
                style={{ transitionDelay: `${(index + 4) * 100}ms` }}
              >
                <div className="text-2xl sm:text-3xl mb-1 sm:mb-2">{feature.icon}</div>
                <h3 className="text-white font-semibold text-sm sm:text-base mb-1">{feature.title}</h3>
                <p className="text-gray-400 text-xs sm:text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>

          {/* Call to Action Buttons */}
          <div 
            className={`flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 mt-8 sm:mt-12 px-4 transform transition-all duration-1000 delay-700 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <button 
              onClick={scrollToProducts}
              className={`group relative px-6 sm:px-8 py-2.5 sm:py-3 overflow-hidden rounded-full 
                bg-red-600 text-white font-semibold transition-all hover:bg-red-700 w-full sm:w-auto
                ${isScrolling ? 'animate-pulse' : ''}`}
              aria-label="Explore our products"
            >
              <span className="relative z-10 flex items-center justify-center">
                Explore Products
                {/* Animated down arrow */}
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className={`h-5 w-5 ml-2 transition-transform duration-300 ${isScrolling ? 'translate-y-1' : ''}`} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-red-600 to-red-800 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left"></div>
            </button>
            <button 
              onClick={() => window.open('https://wa.me/919158235932?text=I%20would%20like%20to%20get%20a%20quote%20for%20a%20custom%203D%20printing%20project', '_blank')}
              className="px-6 sm:px-8 py-2.5 sm:py-3 rounded-full border-2 border-white/30 text-white font-semibold hover:bg-white/10 hover:border-white/50 transition-all w-full sm:w-auto"
              aria-label="Contact us for a quote"
            >
              Get a Quote
            </button>
          </div>
          
          {/* Scroll Indicator - Clickable */}
          <div 
            className={`hidden sm:flex flex-col items-center mt-12 opacity-60 hover:opacity-100 transition-opacity cursor-pointer ${
              isVisible ? 'opacity-50' : 'opacity-0'
            }`}
            onClick={scrollToProducts}
            aria-hidden="true"
          >
            <p className="text-sm text-white mb-2">Scroll to discover</p>
            <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
              <div className="w-1.5 h-1.5 bg-white rounded-full mt-2 animate-bounce"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Animated Bottom Wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg className="w-full h-12 text-white" viewBox="0 0 1440 48" fill="currentColor">
          <path d="M0 48h1440V12c-120-12-240-12-360 0-120 12-240 12-360 0-120-12-240-12-360 0S120 24 0 36v12z">
            <animate
              attributeName="d"
              values="M0 48h1440V12c-120-12-240-12-360 0-120 12-240 12-360 0-120-12-240-12-360 0S120 24 0 36v12z;
                      M0 48h1440V12c-120 12-240 12-360 0-120-12-240-12-360 0-120 12-240 12-360 0S120 0 0 12v36z;
                      M0 48h1440V12c-120-12-240-12-360 0-120 12-240 12-360 0-120-12-240-12-360 0S120 24 0 36v12z"
              dur="10s"
              repeatCount="indefinite"
            />
          </path>
        </svg>
      </div>
      
      {/* Invisible marker for the next section */}
      <div ref={nextSectionRef} className="h-1"></div>

      {/* CSS Animations - Keeping only the text animations */}
      <style jsx>{`
        @keyframes gradient-x {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        @keyframes gradient-x-reverse {
          0% { background-position: 100% 50%; }
          50% { background-position: 0% 50%; }
          100% { background-position: 100% 50%; }
        }
        
        .animate-gradient-x {
          background-size: 200% 200%;
          animation: gradient-x 4s linear infinite;
        }
        
        .animate-gradient-x-reverse {
          background-size: 200% 200%;
          animation: gradient-x-reverse 4s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default ProductHero;